/**
 * period.js
 * Historical Analytics Layer — Dashboard Period resolver.
 *
 * This is the ONLY place "today's date" is ever touched anywhere in the
 * Historical Analytics stack. Every provider (transactions.js,
 * releases.js) and the Aggregator (historyAggregator.js) are Stateless
 * with respect to "what day is it" — they only consume a fully-formed
 * HistoricalContext handed to them by the caller. This file exists to
 * build that context in exactly one place, so the rule for "what counts
 * as the current period" lives in a single, easily-replaceable location.
 *
 * RULE (explicitly agreed — no Month-End Close workflow exists in
 * CostSpace today, so this uses calendar months only):
 *   currentPeriodEnd  = last day of the PREVIOUS calendar month
 *   previousPeriodEnd = last day of the month before that
 *
 * Example: any day in August → currentPeriodEnd = 31 July,
 *          previousPeriodEnd = 30 June.
 * This stays constant for the entire month, so KPI trends don't shift
 * day-to-day — only when the calendar rolls into a new month.
 *
 * If CostSpace introduces a real Month-End Close workflow in the future,
 * only this resolver needs to change (e.g. to read a "last closed month"
 * value from a new table) — every consumer (Dashboard, Executive Report,
 * future Analytics) keeps working unmodified, since they only ever see
 * the resulting DashboardPeriod shape, never today's date itself.
 */

/**
 * @typedef {Object} DashboardPeriod
 * @property {string} label - human-readable, e.g. "July 2026 vs June 2026"
 * @property {string} currentPeriodEnd  - 'YYYY-MM-DD', last day of the previous calendar month
 * @property {string} previousPeriodEnd - 'YYYY-MM-DD', last day of the month before that
 */

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December']

/**
 * Last calendar day of a given (year, month) as 'YYYY-MM-DD'. Built
 * manually rather than via toISOString() to avoid a real timezone bug
 * found during Historical Analytics validation: toISOString() shifts by
 * the local UTC offset, silently producing the wrong day (e.g.
 * "2026-08-30" instead of "2026-08-31") for any user east of UTC.
 *
 * @param {number} year
 * @param {number} month1based - 1-12
 */
function lastDayOfMonth(year, month1based) {
  const lastDay = new Date(year, month1based, 0).getDate() // local-time day count only, no ISO conversion
  return `${year}-${String(month1based).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
}

/**
 * Resolves the current DashboardPeriod per the calendar-month rule
 * above. Pure function — the only "impurity" is defaulting to
 * `new Date()` when no reference date is supplied, kept as an optional
 * parameter specifically so callers (and tests) can pin a fixed date
 * instead of relying on the real system clock.
 *
 * @param {Date} [referenceDate] - defaults to now; pass an explicit Date
 *   to resolve the period as of a different moment (e.g. for tests).
 * @returns {DashboardPeriod}
 */
export function resolveDashboardPeriod(referenceDate = new Date()) {
  const y = referenceDate.getFullYear()
  const m = referenceDate.getMonth() + 1 // 1-based

  // currentPeriodEnd = last day of the PREVIOUS calendar month relative
  // to referenceDate. Rolling back one month means rolling back the year
  // too when referenceDate falls in January.
  const currentYear = m === 1 ? y - 1 : y
  const currentMonth = m === 1 ? 12 : m - 1
  const currentPeriodEnd = lastDayOfMonth(currentYear, currentMonth)

  // previousPeriodEnd = last day of the month before THAT.
  const previousYear = currentMonth === 1 ? currentYear - 1 : currentYear
  const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1
  const previousPeriodEnd = lastDayOfMonth(previousYear, previousMonth)

  return {
    label: `${MONTH_NAMES[currentMonth - 1]} ${currentYear} vs ${MONTH_NAMES[previousMonth - 1]} ${previousYear}`,
    currentPeriodEnd,
    previousPeriodEnd,
  }
}

/**
 * Wraps resolveDashboardPeriod() into the full HistoricalContext shape
 * historyAggregator.js expects (mode: 'calendar' + the calendar object).
 * Consumers should generally call this directly rather than assembling
 * the context manually.
 *
 * @param {Date} [referenceDate]
 * @returns {import('./historyAggregator').HistoricalContext}
 */
export function resolveDashboardContext(referenceDate = new Date()) {
  const period = resolveDashboardPeriod(referenceDate)
  return {
    mode: 'calendar',
    label: period.label,
    calendar: {
      currentPeriodEnd: period.currentPeriodEnd,
      previousPeriodEnd: period.previousPeriodEnd,
    },
  }
}
